import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Approval status for the signed-in user. This is the single gate every
 * account passes through — email/password AND Google/Apple — so social
 * sign-in can't sneak past the invite wall.
 */
export const getAccessStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { approveFromAllowlist, expireTrialIfDue } = await import("./access.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("id, email, approved, plan, plan_expires_at, daily_touch_target")
      .eq("id", context.userId)
      .maybeSingle();

    const email = (profile as any)?.email ?? (context.claims as any)?.email ?? null;
    let approved = !!(profile as any)?.approved;

    if (!approved) {
      const granted = await approveFromAllowlist(context.userId, email);
      if (granted) approved = true;
    } else {
      await expireTrialIfDue(profile);
    }

    if (approved) return { approved: true as const, waitlistPosition: null, email };

    const { waitlistPosition } = await import("@/lib/waitlist.server");

    // Waitlist position: same helper the confirmation email uses, so the
    // number in the inbox matches the number on screen.
    const { data: mine } = await supabaseAdmin
      .from("access_requests")
      .select("created_at")
      .eq("email", email ?? "")
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    return {
      approved: false as const,
      waitlistPosition: await waitlistPosition(supabaseAdmin, {
        cutoff: (mine as any)?.created_at ?? new Date().toISOString(),
      }),
      hasRequest: !!mine,
      email,
    };
  });

/** Redeem an invite code from inside the app (used by the waitlist screen). */
export const redeemInviteCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ code: z.string().trim().min(1) }).parse(d))
  .handler(async ({ data, context }) => {
    const { redeemCode } = await import("./access.server");
    return redeemCode(context.userId, data.code);
  });
